/**
 * Self-serve provider sign-up, client Q1 and Q2 (2026-09-09).
 *
 *   Q1  "Self Serve. Please add a Sign up option within the sign in page."
 *   Q2  each doctor is their own practice, billed individually.
 *
 * Role first, then the account. Doctor and nurse take different paths after
 * this screen, a nurse never becomes the ordering provider, so
 * asking which one they are before anything else is what makes the rest of the
 * flow legible rather than a form full of conditional fields.
 *
 * Only what is needed to create the account is collected here. NPI, licence
 * and DEA come afterwards on the application page: nobody should have to find
 * their DEA number before they can log in for the first time.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button, Field, inputClass } from '../components/ui';
import { IconCheck } from '../components/icons';

type ProviderType = 'DOCTOR' | 'NURSE';

const ROLES: {
  type: ProviderType;
  label: string;
  blurb: string;
  next: string;
}[] = [
  {
    type: 'DOCTOR',
    label: 'Doctor',
    blurb: 'MD or DO. Orders labs directly once your credentials are approved.',
    next: 'You will be asked for your NPI, state licence and practice details.',
  },
  {
    type: 'NURSE',
    label: 'Nurse',
    blurb:
      'NP, APRN or RN. Enrols patients. Lab orders are placed by the MCOS Clinical team, not under your licence.',
    next: 'You will be asked for your license number, the states you are licensed in, and your practice address.',
  },
];

export function SignUpPage() {
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [providerType, setProviderType] = useState<ProviderType | null>(null);
  const [legalFirstName, setFirst] = useState('');
  const [legalLastName, setLast] = useState('');
  const [credentials, setCredentials] = useState('');
  const [practiceName, setPracticeName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const chosen = ROLES.find((r) => r.type === providerType);

  // Mirrors the server's rule, so the failure is caught before the round trip.
  const passwordOk =
    password.length >= 12 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /[0-9]/.test(password);

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!providerType) return;
    setError(null);
    setBusy(true);
    try {
      await signUp({
        providerType,
        legalFirstName: legalFirstName.trim(),
        legalLastName: legalLastName.trim(),
        credentials: credentials.trim(),
        practiceName: practiceName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        password,
      });
      navigate('/application', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-up failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-ink/5 px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <h1 className="text-xl font-semibold text-ink">Create your account</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            Register your practice on the MCOS Wellness Platform.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="glass-standard rounded-md2 p-6 space-y-5"
        >
          {/* --- role first --- */}
          <fieldset>
            <legend className="block text-xs font-medium text-ink mb-2">
              I am a<span className="text-red-500 ml-0.5">*</span>
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {ROLES.map((role) => {
                const active = providerType === role.type;
                return (
                  <button
                    key={role.type}
                    type="button"
                    onClick={() => setProviderType(role.type)}
                    aria-pressed={active}
                    className={`rounded-sm2 border p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-accent)] ${
                      active
                        ? 'border-[var(--brand-accent)] bg-ink/5 ring-1 ring-[var(--brand-accent)]'
                        : 'border-ink/10 hover:bg-ink/5'
                    }`}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-ink">
                      {role.label}
                      {active && (
                        <span className="text-[var(--brand-accent)]">
                          <IconCheck size={14} />
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block text-[11px] leading-snug text-ink-muted">
                      {role.blurb}
                    </span>
                  </button>
                );
              })}
            </div>
            {chosen && (
              <p className="mt-2 text-[11px] text-ink-muted">Next: {chosen.next}</p>
            )}
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Legal first name" required>
              <input
                required
                autoComplete="given-name"
                value={legalFirstName}
                onChange={(e) => setFirst(e.target.value)}
                className={inputClass}
              />
            </Field>
            <Field label="Legal last name" required>
              <input
                required
                autoComplete="family-name"
                value={legalLastName}
                onChange={(e) => setLast(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>

          <Field label="Credentials" hint="As they should appear on a report, MD, DO, NP, APRN." required>
            <input
              required
              value={credentials}
              onChange={(e) => setCredentials(e.target.value)}
              className={inputClass}
              placeholder="MD"
            />
          </Field>

          <Field
            label="Practice name"
            hint="Your practice is created for you. This becomes its name on every report."
            required
          >
            <input
              required
              value={practiceName}
              onChange={(e) => setPracticeName(e.target.value)}
              className={inputClass}
              placeholder="Riverbend Family Wellness"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Email" required>
              <input
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="you@practice.example"
              />
            </Field>
            <Field label="Phone" required>
              <input
                type="tel"
                required
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClass}
                placeholder="512-555-0100"
              />
            </Field>
          </div>

          <Field
            label="Password"
            hint="At least 12 characters, with an uppercase letter, a lowercase letter and a number."
            error={password.length > 0 && !passwordOk ? 'Does not meet the requirements yet.' : undefined}
            required
          >
            <input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
            />
          </Field>

          {error && (
            <p
              role="alert"
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm2 px-3 py-2"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            loading={busy}
            disabled={!providerType || !passwordOk}
            className="w-full"
          >
            Create account
          </Button>

          <p className="text-center text-[11px] text-ink-muted">
            Already registered?{' '}
            <Link to="/login" className="font-medium text-[var(--brand-accent)] hover:underline">
              Sign in
            </Link>
          </p>
        </form>

        <p className="mt-5 text-center text-[11px] leading-relaxed text-ink-muted">
          Creating an account does not grant lab-ordering access. Your credentials are reviewed by
          the medical director first.
        </p>
      </div>
    </div>
  );
}
